import { assertPenpotTemplateReady } from './penpot-template-seed.mjs';
import { validateSectionComponents } from './components.mjs';

const failure = (code, message, cause) => Object.assign(new Error(message, cause ? { cause } : undefined), { code });
const samePage = (left, right) => left && right && ['origin', 'teamId', 'fileId', 'pageId'].every(key => left[key] === right[key]);
const validateRecords = records => validateSectionComponents(records, 'components', (_path, message) => {
  throw failure('INVALID_PENPOT_COMPONENT_RECORD', message);
});

async function changeComponent(command, { editor, componentId, scene, record, binding = null, assertCurrent = () => {} }) {
  const status = await assertPenpotTemplateReady(editor);
  const source = editor.getSource();
  assertCurrent();
  if (!source || !Number.isSafeInteger(status.contentRevision)) throw failure('PENPOT_COMPONENT_NOT_READY', '원본 페이지의 편집 상태를 확인할 수 없습니다.');
  const input = { componentId, fileId: source.fileId, pageId: source.pageId,
    expectedContentRevision: status.contentRevision, binding: structuredClone(binding) };
  if (source.boardId) input.boardId = source.boardId;
  if (command === 'upsert_data_component') {
    if (!record || record.id !== componentId || Object.hasOwn(record, 'binding')) throw failure('INVALID_PENPOT_COMPONENT_RECORD', '컴포넌트 설정과 계산 기록이 필요합니다.');
    validateRecords([{ ...record, binding: binding ?? {} }]);
    input.scene = structuredClone(scene);
    input.record = structuredClone(record);
  }
  let transactionId = null;
  try {
    const result = await editor.execute(command, input);
    transactionId = result?.transactionId;
    if (typeof transactionId !== 'string' || !transactionId || !samePage(source, result.source)
        || result.binding?.componentId !== componentId || typeof result.binding.boardId !== 'string'
        || !result.binding.bindings || typeof result.binding.bindings !== 'object') {
      throw failure('INVALID_PENPOT_COMPONENT_RESULT', '원본 편집기가 올바른 컴포넌트 연결과 복구 기록을 반환하지 않았습니다.');
    }
    await editor.flush();
    assertCurrent();
    if (!samePage(source, editor.getSource())) throw failure('PENPOT_COMPONENT_CONFLICT', '데이터를 반영하는 동안 열린 페이지가 변경되었습니다.');
    let state = 'pending';
    const finish = async operation => {
      if (state === operation) return;
      if (state !== 'pending') throw failure('PENPOT_COMPONENT_TRANSACTION_CLOSED', '이미 종료된 컴포넌트 변경입니다.');
      state = 'working';
      try {
        await editor.execute(operation === 'committed' ? 'commit_scene' : 'rollback_scene', { transactionId });
        state = operation;
      } catch (error) { state = 'pending'; throw error; }
    };
    return { binding: structuredClone(result.binding), commit: () => finish('committed'), rollback: () => finish('restored') };
  } catch (cause) {
    if (transactionId) {
      try { await editor.execute('rollback_scene', { transactionId }); }
      catch (rollbackError) {
        throw failure('PENPOT_COMPONENT_ROLLBACK_FAILED', `컴포넌트 변경을 복구하지 못했습니다. 현재 원본을 확인해 주세요. ${rollbackError.message}`, cause);
      }
    }
    throw cause;
  }
}

/** The caller records section metadata before commit; rollback restores native undo state on failure. */
export const upsertPenpotDataComponent = options => changeComponent('upsert_data_component', options);
export const removePenpotDataComponent = options => changeComponent('remove_data_component', options);
export const detachPenpotDataComponent = options => changeComponent('detach_data_component', options);

/** Component settings follow the same native undo history as their actual layers. */
export async function readPenpotDataComponents({ editor, assertCurrent = () => {} }) {
  await assertPenpotTemplateReady(editor);
  const source = editor.getSource();
  assertCurrent();
  if (!source) throw failure('PENPOT_COMPONENT_NOT_READY', '원본 페이지를 확인할 수 없습니다.');
  const input = { fileId: source.fileId, pageId: source.pageId };
  if (source.boardId) input.boardId = source.boardId;
  const result = await editor.execute('get_data_components', input);
  assertCurrent();
  if (!samePage(source, editor.getSource())) throw failure('PENPOT_COMPONENT_CONFLICT', '컴포넌트 기록을 읽는 동안 열린 페이지가 변경되었습니다.');
  validateRecords(result?.components);
  return structuredClone(result.components);
}
